import { useEffect, useMemo, useRef, useState } from 'react';
import { T, useIsDesktop, Bone } from '../reportTokens';
import { ReportSectionExportButton } from './ReportExportButton';

/* ─── Generic sortable / searchable / paginated table card ───────────────── */
export interface Col {
    key: string;
    label: string;
    align?: 'left' | 'right';
    sortable?: boolean;
    sortKey?: string;
    value?: (r: any) => number | string;
    render: (r: any) => React.ReactNode;
    footer?: (rows: any[]) => React.ReactNode;
    // Optional column width hint (e.g. '12%'). Leave unset to keep the default
    // content-driven auto-layout — only set when a table needs deterministic
    // column proportions (e.g. to stop table-layout:auto over-allocating space
    // to a short column and creating a visual gap next to it).
    width?: string | number;
}
export interface ServerPagination {
    totalElements: number;
    page: number;       // 1-based
    pageSize: number;
    onPageChange: (page: number, pageSize: number) => void;
}
export interface ServerSort {
    sortBy: string;
    sortDir: 'asc' | 'desc';
    onSortChange: (sortBy: string, sortDir: 'asc' | 'desc') => void;
}

/* ─── Section-level table shimmer (search / pagination reloads within a card) ─── */
export function TableCardShimmer({ cols, rows = 6, isDesktop }: { cols: number; rows?: number; isDesktop: boolean }) {
    if (!isDesktop) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, background: T.surface }}>
                        <Bone h={13} w={120} mb={8} />
                        <Bone h={11} w={160} />
                    </div>
                ))}
            </div>
        );
    }
    return (
        <div style={{ padding: '4px 0' }}>
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `1px solid ${T.divider}` }}>
                    <Bone w={140} h={13} r={4} />
                    {Array.from({ length: cols - 1 }).map((_, j) => (
                        <Bone key={j} w={null} h={13} r={4} style={{ flex: 1 }} />
                    ))}
                </div>
            ))}
        </div>
    );
}

/* ─── Card chrome (title/subtitle + right-side actions) shared by every table card ─── */
export function SectionCard({ title, subtitle, right, children, id }: any) {
    const isDesktop = useIsDesktop();
    return (
        <div id={id} style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: isDesktop ? 22 : 14, boxShadow: T.cardShadow }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <div>
                    <div style={{ fontSize: isDesktop ? 15 : 13, fontWeight: 700, color: T.t1 }}>{title}</div>
                    {subtitle && <div style={{ fontSize: isDesktop ? 11 : 10, color: T.t3, marginTop: 2 }}>{subtitle}</div>}
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

export function DataTableCard({
    id, title, subtitle, columns, rows, defaultCompare, gaTable, onTrack,
    searchValue, mobileCard, showFooter, exportConfig, serverPagination, onSearch, searchTerm, loading, serverSort,
}: any) {
    const isDesktop = useIsDesktop();
    const track = (event: string, params: Record<string, any> = {}) => onTrack?.(event, params);
    const [localSearch, setLocalSearch] = useState('');
    const [sort, setSort] = useState<{ key: string; dir: 'desc' | 'asc' } | null>(null);
    const [localPage, setLocalPage] = useState(0);
    const [localPageSize, setLocalPageSize] = useState(10);
    const [pageSizeMenuOpen, setPageSizeMenuOpen] = useState(false);
    const pageSizeRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!pageSizeMenuOpen) return;
        const h = (e: MouseEvent) => { if (pageSizeRef.current && !pageSizeRef.current.contains(e.target as Node)) setPageSizeMenuOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [pageSizeMenuOpen]);

    const sp: ServerPagination | undefined = serverPagination;
    // When onSearch is provided, search is server-side — skip local filtering.
    const isServerSearch = typeof onSearch === 'function';
    const search = isServerSearch ? (searchTerm ?? '') : localSearch;

    const filtered = useMemo(() => {
        if (isServerSearch) return rows; // server already filtered
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r: any) => String(searchValue ? searchValue(r) : '').toLowerCase().includes(q));
    }, [rows, search, searchValue, isServerSearch]);

    const ss: ServerSort | undefined = serverSort;

    const sorted = useMemo(() => {
        if (ss) return filtered; // server already returns rows in sorted order
        const arr = [...filtered];
        if (sort) {
            const col = (columns as Col[]).find((c) => c.key === sort.key);
            if (col?.value) {
                arr.sort((a, b) => {
                    const av = col.value!(a), bv = col.value!(b);
                    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
                    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
                    return 0;
                });
                return arr;
            }
        }
        if (defaultCompare) arr.sort(defaultCompare);
        return arr;
    }, [filtered, sort, columns, defaultCompare, ss]);

    // Server-side mode: rows IS the current page, use server totals/page values.
    // Client-side mode: slice locally as before.
    const total = sp ? sp.totalElements : sorted.length;
    const pageSize = sp ? sp.pageSize : localPageSize;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = sp ? sp.page - 1 : Math.min(localPage, totalPages - 1);
    const startIdx = total === 0 ? 0 : safePage * pageSize + 1;
    const endIdx = Math.min((safePage + 1) * pageSize, total);
    const visible = sp ? sorted : sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);

    useEffect(() => { if (!sp) setLocalPage(0); }, [localSearch, localPageSize, rows]);

    const clickSort = (col: Col) => {
        if (col.sortable === false) return;
        if (ss) {
            const targetKey = col.sortKey || col.key;
            const dir: 'asc' | 'desc' = ss.sortBy !== targetKey ? 'desc' : ss.sortDir === 'desc' ? 'asc' : 'desc';
            track('maghil_reports_table_sort', { table: gaTable, sort_column: targetKey, sort_direction: dir });
            ss.onSortChange(targetKey, dir);
            return;
        }
        if (!col.value) return;
        setSort((prev) => {
            const dir = !prev || prev.key !== col.key ? 'desc' : prev.dir === 'desc' ? 'asc' : null as any;
            track('maghil_reports_table_sort', { table: gaTable, sort_column: col.key, sort_direction: dir || 'reset' });
            return dir ? { key: col.key, dir } : null;
        });
    };

    const handleSearchChange = (val: string) => {
        if (isServerSearch) {
            onSearch(val);
        } else {
            setLocalSearch(val);
        }
        track('maghil_reports_table_search', { table: gaTable, search_query: val });
    };

    const SearchBox = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: isDesktop ? '6px 10px' : '5px 8px', borderRadius: 8, background: T.hover, border: `1px solid ${T.cardBorder}`, minWidth: isDesktop ? 200 : 0, flex: isDesktop ? undefined : 1 }}>
            <span className="material-symbols-rounded" style={{ fontSize: isDesktop ? 16 : 14, color: T.t3 }}>search</span>
            <input
                className="rpt-filter-search-input"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by name"
            />
        </div>
    );

    const handlePageSizeChange = (newSize: number) => {
        if (sp) {
            sp.onPageChange(1, newSize);
        } else {
            setLocalPageSize(newSize);
        }
        track('maghil_reports_table_page_size', { table: gaTable, page_size: newSize });
    };

    const handlePrev = () => {
        if (sp) sp.onPageChange(sp.page - 1, sp.pageSize);
        else setLocalPage((p) => Math.max(0, p - 1));
    };

    const handleNext = () => {
        if (sp) sp.onPageChange(sp.page + 1, sp.pageSize);
        else setLocalPage((p) => Math.min(totalPages - 1, p + 1));
    };

    const pagBtnStyle = (disabled: boolean) => ({
        padding: isDesktop ? '4px 10px' : '3px 8px',
        borderRadius: isDesktop ? 6 : 5,
        border: `1px solid ${T.cardBorder}`,
        background: disabled ? T.hover : T.surface,
        color: disabled ? T.t3 : T.t1,
        fontSize: isDesktop ? 11 : 10,
        fontWeight: 600 as const,
        cursor: disabled ? 'default' : 'pointer',
    });
    const showSizeSelector = total > 10;
    const countText = `Showing ${startIdx}–${endIdx} of ${total} items`;
    const PageSizeSelector = showSizeSelector ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: isDesktop ? 11 : 10, color: T.t3 }}>
            Rows per page:
            <div ref={pageSizeRef} style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                    type="button"
                    className="labor-pager-size-trigger"
                    onClick={() => setPageSizeMenuOpen((o) => !o)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: isDesktop ? 11 : 10, fontWeight: 600, color: T.t1, fontFamily: T.font, padding: isDesktop ? '3px 6px' : '2px 4px', borderRadius: 6, border: `1px solid ${pageSizeMenuOpen ? T.accent : T.cardBorder}`, background: T.surface, cursor: 'pointer' }}
                >
                    {pageSize}
                    <span className="material-symbols-rounded" style={{ fontSize: isDesktop ? 14 : 12 }}>{pageSizeMenuOpen ? 'expand_less' : 'expand_more'}</span>
                </button>
                {pageSizeMenuOpen && (
                    <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 4, minWidth: 56, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 50, overflow: 'hidden' }}>
                        {[10, 20, 30].map((s) => {
                            const active = s === pageSize;
                            return (
                                <div
                                    key={s}
                                    onClick={() => { handlePageSizeChange(s); setPageSizeMenuOpen(false); }}
                                    onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = T.hover; }}
                                    onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                                    style={{ padding: isDesktop ? '6px 12px' : '5px 10px', fontSize: isDesktop ? 11 : 10, fontWeight: active ? 700 : 500, color: active ? T.accent : T.t1, background: active ? T.accentSoft : 'transparent', cursor: 'pointer', fontFamily: T.font, textAlign: 'center' }}
                                >
                                    {s}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    ) : null;
    const Pager = total > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
            <div style={{ fontSize: isDesktop ? 11 : 10, color: T.t3 }}>{countText}</div>
            {(showSizeSelector || totalPages > 1) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: isDesktop ? 12 : 8, flexWrap: 'wrap' }}>
                    {PageSizeSelector}
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button type="button" className="labor-pager-btn" onClick={handlePrev} disabled={safePage === 0} style={pagBtnStyle(safePage === 0)}>← Prev</button>
                            <span style={{ fontSize: isDesktop ? 11 : 10, color: T.t2, padding: '4px 6px' }}>{safePage + 1} / {totalPages}</span>
                            <button type="button" className="labor-pager-btn" onClick={handleNext} disabled={safePage >= totalPages - 1} style={pagBtnStyle(safePage >= totalPages - 1)}>Next →</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const HeaderRight = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {SearchBox}
            {exportConfig && (
                <ReportSectionExportButton
                    title={exportConfig.title}
                    filename={exportConfig.filename}
                    buildExport={exportConfig.buildExport}
                    dateRangeLabel={exportConfig.dateRangeLabel}
                    outletScoped={exportConfig.outletScoped}
                />
            )}
        </div>
    );

    return (
        <SectionCard id={id} title={title} subtitle={subtitle} right={HeaderRight}>
            {loading ? (
                <TableCardShimmer cols={(columns as Col[]).length} isDesktop={isDesktop} />
            ) : total === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: T.t3, fontSize: 13 }}>
                    {search ? `No results match "${search.trim()}"` : 'No records found for the selected period.'}
                </div>
            ) : isDesktop ? (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: T.font }}>
                        <thead>
                            <tr>
                                {(columns as Col[]).map((c) => {
                                    const active = ss ? ss.sortBy === (c.sortKey || c.key) : sort?.key === c.key;
                                    const activeDir = ss ? ss.sortDir : sort?.dir;
                                    const clickable = c.sortable !== false && (ss || c.value);
                                    return (
                                        <th key={c.key}
                                            style={{
                                                textAlign: c.align || 'left', fontSize: 11, fontWeight: 600, color: T.t3,
                                                textTransform: 'uppercase', letterSpacing: 0.4, padding: '0 10px 10px',
                                                borderBottom: `1px solid ${T.divider}`, whiteSpace: 'nowrap', width: c.width,
                                            }}>
                                            <span onClick={() => clickSort(c)}
                                                style={{ cursor: clickable ? 'pointer' : 'default', userSelect: 'none' }}>
                                                {c.label}
                                                {active && <span style={{ marginLeft: 3 }}>{activeDir === 'desc' ? '▾' : '▴'}</span>}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((r: any, i: number) => (
                                <tr key={r.id || i} style={{ borderBottom: `1px solid ${T.divider}` }}>
                                    {(columns as Col[]).map((c) => (
                                        <td key={c.key} style={{ textAlign: c.align || 'left', fontSize: 12, color: T.t1, padding: '10px', whiteSpace: 'nowrap', width: c.width }}>{c.render(r)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                        {showFooter && (
                            <tfoot>
                                <tr>
                                    {(columns as Col[]).map((c, ci) => (
                                        <td key={c.key} style={{ textAlign: c.align || 'left', fontSize: 12, fontWeight: 700, color: T.t1, padding: '12px 10px', borderTop: `2px solid ${T.divider}`, width: c.width }}>
                                            {ci === 0 ? 'Total' : c.footer ? c.footer(filtered) : ''}
                                        </td>
                                    ))}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {visible.map((r: any, i: number) => <div key={r.id || i}>{mobileCard(r)}</div>)}
                </div>
            )}
            {!loading && Pager}
        </SectionCard>
    );
}
